// app/core/auth/interceptors/auth.interceptor.ts
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, catchError, EMPTY, throwError } from 'rxjs';

let isRedirectingOn401 = false; // prevent redirect storms

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Don’t attach token to auth endpoints (login, refresh, etc.)
  const isAuthCall = /\/auth\//.test(req.url);

  // Attach Authorization if we have a valid token and it’s not an auth call
  let newReq = req;
  if (auth.accessToken && !AuthUtils.isTokenExpired(auth.accessToken) && !isAuthCall) {
    newReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${auth.accessToken}`),
    });
  }

  return next(newReq).pipe(
    catchError((error) => {
      const isStaticAsset =
        /\.(js|css|chunk|png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot)$/.test(req.url) ||
        req.url.includes('chunk-') ||
        req.url.includes('assets/') ||
        error.headers?.get('content-type')?.includes('text/html'); // If server returns HTML on 401
      // Only act on 401 from non-auth calls
      if (error instanceof HttpErrorResponse && error.status === 401 && !isAuthCall && !isStaticAsset) {
        if (!isRedirectingOn401) {
          isRedirectingOn401 = true;

          // Clear tokens/session
          auth.signOut(); // your signOut already clears storage

          // Navigate to login with returnUrl; avoid full page reload
          const currentUrl = router.url;
          router
            .navigate(['/sign-in'], {
              queryParams: { returnUrl: currentUrl },
            })
            .finally(() => {
              isRedirectingOn401 = false;
            });
        }

        // Stop the failing stream to avoid additional noise
        return EMPTY;
      }

      // Pass other errors through
      return throwError(() => error);
    }),
  );
};
